// Pet rules: state shape, passive bonus source, attack power.
import { D, type Decimal } from '../core/decimal';
import { PETS, PET_TUNING, type PetDef } from '../data/pets';
import type { GameState } from './state';
import type { ModifierSource } from './stats';

export interface PetState {
  levels: Record<string, number>;
  food: Record<string, number>;
  active: string | null;
  /** Taps since the active pet last struck. */
  tapCounter: number;
}

export function emptyPets(): PetState {
  return { levels: {}, food: {}, active: null, tapCounter: 0 };
}

export function petBonusValue(pet: PetDef, level: number, active: boolean): Decimal {
  const eff = level * (active ? PET_TUNING.activeBonusMult : 1);
  return pet.bonus.kind === 'mult' ? D(1 + pet.bonus.perLevel * eff) : D(pet.bonus.perLevel * eff);
}

export function describePet(pet: PetDef, level: number, active: boolean): string {
  const v = petBonusValue(pet, level, active).toNumber();
  const text = pet.bonus.kind === 'mult' ? `x${v.toFixed(2)}` : `+${Math.round(v * 100)}%`;
  return pet.desc.replace('{v}', text);
}

export function petAttackMult(level: number): number {
  return PET_TUNING.attackBase + PET_TUNING.attackPerLevel * level;
}

export function petSource(): ModifierSource {
  return {
    id: 'pets',
    apply(state: GameState, m) {
      for (const pet of PETS) {
        const level = state.pets.levels[pet.id] ?? 0;
        if (level <= 0) continue;
        const v = petBonusValue(pet, level, state.pets.active === pet.id);
        if (pet.bonus.kind === 'mult') m.mult(pet.bonus.stat, v);
        else m.add(pet.bonus.stat, v.toNumber());
      }
    },
  };
}
